import { BitcoinAddressConverter, BitcoinHashUtils, EthereumBridge, TBTC as TbtcSdk, } from "@keep-network/tbtc-v2.ts";
import { ethers, ZeroAddress } from "ethers";
import { getDefaultProvider, VoidSigner } from "ethers-v5";
import TbtcApi from "../../lib/api/TbtcApi";
import { Hex } from "../../lib/utils";
import Deposit from "./Deposit";
import { BitcoinNetwork } from "../../lib/bitcoin";
/**
 * Represents the tBTC module.
 */
export default class Tbtc {
    #tbtcApi;
    #tbtcSdk;
    #bitcoinDepositor;
    #network;
    constructor(tbtcApi, tbtcSdk, bitcoinDepositor, network) {
        this.#tbtcApi = tbtcApi;
        this.#tbtcSdk = tbtcSdk;
        this.#bitcoinDepositor = bitcoinDepositor;
        this.#network = network;
    }
    /**
     * Initializes the Tbtc module.
     *
     * @param network The Ethereum network.
     * @param ethereumRpcUrl The Ethereum RPC URL.
     * @param tbtcApiUrl The tBTC API URL.
     * @param bitcoinDepositor The Bitcoin depositor contract handle.
     * @returns A Promise that resolves to an instance of Tbtc.
     */
    static async initialize(network, ethereumRpcUrl, tbtcApiUrl, bitcoinDepositor) {
        const tbtcApi = new TbtcApi(tbtcApiUrl);
        const signer = new VoidSigner(ZeroAddress, getDefaultProvider(ethereumRpcUrl));
        const tbtcSdk = network === BitcoinNetwork.Mainnet
            ? await TbtcSdk.initializeMainnet(signer)
            : await TbtcSdk.initializeSepolia(signer);
        return new Tbtc(tbtcApi, tbtcSdk, bitcoinDepositor, network);
    }
    /**
     * Function to initialize a tBTC deposit. It submits deposit data to the tBTC
     * API and returns the deposit object.
     * @param depositOwner Ethereum address of the deposit owner.
     * @param bitcoinRecoveryAddress P2PKH or P2WPKH Bitcoin address that can be
     *        used for emergency recovery of the deposited funds.
     * @param referral Deposit referral number.
     * @param loggedInUser Identifier of the user who initiated the deposit.
     */
    async initiateDeposit(depositOwner, bitcoinRecoveryAddress, referral, loggedInUser) {
        if (!depositOwner || !bitcoinRecoveryAddress) {
            throw new Error("Ethereum or Bitcoin address is not available");
        }
        const extraData = this.#bitcoinDepositor.encodeExtraData(depositOwner, referral);
        const tbtcDeposit = await this.#tbtcSdk.deposits.initiateDepositWithProxy(bitcoinRecoveryAddress, this.#bitcoinDepositor, extraData);
        const receipt = tbtcDeposit.getReceipt();
        const revealData = {
            address: depositOwner.identifierHex,
            revealInfo: {
                depositor: receipt.depositor.identifierHex,
                blindingFactor: receipt.blindingFactor.toString(),
                walletPublicKeyHash: receipt.walletPublicKeyHash.toString(),
                refundPublicKeyHash: receipt.refundPublicKeyHash.toString(),
                refundLocktime: receipt.refundLocktime.toString(),
                extraData: receipt.extraData.toString(),
            },
            metadata: {
                depositOwner: depositOwner.identifierHex,
                referral,
            },
            application: "acre",
            loggedInUser,
        };
        const revealSaved = await this.#tbtcApi.saveReveal(revealData);
        if (!revealSaved)
            throw new Error("Reveal not saved properly in the database");
        return new Deposit(this.#tbtcApi, tbtcDeposit, revealData);
    }
    /**
     * @param depositOwner Depositor as EVM-chain identifier.
     * @returns All owner deposits, including queued deposits.
     */
    async getDepositsByOwner(depositOwner) {
        const deposits = await this.#tbtcApi.getDepositsByOwner(depositOwner);
        return deposits.map((deposit) => ({
            status: deposit.status,
            initialAmount: BigInt(deposit.initialAmount),
            depositKey: ethers.solidityPackedKeccak256(["bytes32", "uint32"], [
                Hex.from(deposit.txHash).reverse().toPrefixedString(),
                deposit.outputIndex,
            ]),
            txHash: deposit.txHash,
            initializedAt: deposit.createdAt,
        }));
    }
    /**
     * Requests a redemption of tBTC token into BTC. It builds the redemption
     * data and handles the redemption request through the provided redeemer
     * proxy.
     *
     * The live tBTC wallet and its main UTXO are resolved client-side (via the
     * tBTC SDK's Electrum client), because the resulting payload goes straight to
     * `Bridge.requestRedemption` - see `sdk/src/lib/redeemer-proxy.ts`.
     * @param destinationBitcoinAddress Bitcoin address the redeemed BTC should be
     *        sent to. Only P2PKH, P2WPKH, P2SH, and P2WSH address types are
     *        supported.
     * @param tbtcAmount The amount to be redeemed in 1e18 tBTC token precision.
     *        Used ONLY to size tBTC wallet selection - the transaction itself
     *        approves AcreBTC shares.
     * @param redeemerProxy Object implementing functions required to route tBTC
     *        redemption requests through the tBTC bridge.
     * @returns The transaction hash and redemption key.
     */
    async initiateRedemption(destinationBitcoinAddress, tbtcAmount, redeemerProxy) {
        const { targetChainTxHash, walletPublicKey } = await this.#tbtcSdk.redemptions.requestRedemptionWithProxy(destinationBitcoinAddress, tbtcAmount, redeemerProxy);
        const redeemerOutputScript = BitcoinAddressConverter.addressToOutputScript(destinationBitcoinAddress, this.#network);
        const redemptionKey = EthereumBridge.buildRedemptionKey(BitcoinHashUtils.computeHash160(walletPublicKey), redeemerOutputScript);
        return {
            transactionHash: targetChainTxHash.toPrefixedString(),
            redemptionKey,
        };
    }
}
//# sourceMappingURL=Tbtc.js.map